import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FinanceQueries } from '@frontend/api-finance';
import { FreeTrialSubscriptionQueries, FreeTrialSubscriptionTypes } from '@frontend/api-free-trial-subscription';
import { getUser } from '@frontend/auth-helpers';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate, AnyRoute } from '@frontend/weave-router';
import { Text, useAlert, useModalControl } from '@frontend/design-system';
import { SelfUpgradeFeatureNameMap } from '../../../constants/self-upgrade';
import { getFeatureWiseTrackingId, SelfUpgradeModalTrackingIds } from '../../../constants/tracking-ids';
import { checkCanSelfSubscribe, getBillingPriceText } from '../../../self-upgrade.util';
import { FeatureSelfSubscribeModalProps, LocationSubscriptionWithBillingData } from '../../../types';
import { FeatureSubscriptionChangeCard } from '../../feature-subscription-change-card';
import { SelfUpgradeBaseModal } from '../../feature-upgrade-action-modal/self-upgrade-modal/SelfUpgradeBaseModal';
import { SelfUpgradeFailModal } from '../../feature-upgrade-action-modal/self-upgrade-modal/SelfUpgradeFailModal';
import { SelfUpgradeLocationSelectionTable } from '../../feature-upgrade-action-modal/self-upgrade-modal/SelfUpgradeLocationSelectionTable';
import { SelfUpgradeOverdueBalance } from '../../feature-upgrade-action-modal/self-upgrade-modal/SelfUpgradeOverdueBalanceModal';
import { SelfUpgradeSuccessModal } from '../../feature-upgrade-action-modal/self-upgrade-modal/SelfUpgradeSuccessModal';
import { SubscribeTermsModal } from '../../subscribe-terms-modal/SubscribeTermsModal';
import {
  FORMS_MULTI_ONBOARDER_SCHEDULING_URL,
  FORMS_PAGE_URL,
  FORMS_PRICE,
  FORMS_SINGLE_ONBOARDER_SCHEDULING_URL,
} from './constants';

const FEATURE_ENUM = Feature.FORMS;

export const FormsSelfSubscribeModal: FC<FeatureSelfSubscribeModalProps> = ({
  selfSubscribeEligibilityData,
  onUpgradeSuccess,
  ...modalProps
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'forms-self-upgrade-modal' });
  const { accessibleLocationData } = useAppScopeStore();
  const alerts = useAlert();
  const navigate = useNavigate();

  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  const [partialFailedLocationIds, setPartialFailedLocationIds] = useState<string[]>([]);
  const selfUpgradeSuccessModalControls = useModalControl();
  const selfUpgradeFailModalControls = useModalControl();
  const overdueBalanceModalControls = useModalControl();
  const subscribeTermsModalControls = useModalControl();

  const locationIds = selfSubscribeEligibilityData.map(({ locationId }) => locationId);
  const { data: multiInvoiceBalanceData, isLoading: isMultiInvoiceBalanceDataLoading } =
    FinanceQueries.useMultiInvoiceBalanceDetails(
      { locationIds },
      { enabled: modalProps.show && locationIds.length > 0 }
    );

  const locationWiseOverdueBalance = useMemo<Record<string, boolean>>(() => {
    if (multiInvoiceBalanceData?.response?.length) {
      return multiInvoiceBalanceData.response.reduce<Record<string, boolean>>((acc, current) => {
        if (current.locationId && !current.error) {
          acc[current.locationId] = Number(current.balanceStatus?.current) > 0;
        }
        return acc;
      }, {});
    }
    return {};
  }, [multiInvoiceBalanceData]);

  const modifiedSelfUpgradeEligibilityData = useMemo<LocationSubscriptionWithBillingData[]>(() => {
    return selfSubscribeEligibilityData.map((data) => ({
      ...data,
      hasOverdueBalance: !!locationWiseOverdueBalance[data.locationId],
    }));
  }, [selfSubscribeEligibilityData, locationWiseOverdueBalance]);

  const showFailedAlert = (partialFailedLocations: string[] = []) => {
    modalProps.onClose();
    alerts.error({
      message: partialFailedLocations.length
        ? t('Upgrade failed for {{count}} location', { count: partialFailedLocations.length })
        : t('Upgrade failed'),
      action: {
        label: partialFailedLocations.length ? t('See Details') : t('Contact Support'),
        onClick: () => {
          setPartialFailedLocationIds(partialFailedLocations);
          selfUpgradeFailModalControls.openModal();
        },
      },
    });
  };

  const { mutateAsync: subscribe, isLoading: isSubscriptionInProgress } =
    FreeTrialSubscriptionQueries.useFeatureMultiSubscribe({
      onSuccess: (response) => {
        const failedToUpgradeLocationIds: string[] =
          response.subscriptionResponse?.filter(({ error }) => !!error).map(({ locationId }) => locationId || '') ||
          ([] as string[]);

        const successfulLocationIds: string[] = (response.subscriptionResponse || [])
          .filter(({ error, locationId }) => !error && locationId)
          .map(({ locationId }) => locationId || '');

        if (!failedToUpgradeLocationIds.length) {
          onUpgradeSuccess({
            successfulLocationIds,
          });
          alerts.success({
            message: t('Success! You now have access to Digital Forms'),
            action: {
              label: t('Go to'),
              onClick: () => {
                navigate({
                  to: FORMS_PAGE_URL as AnyRoute,
                });
              },
            },
          });
          selfUpgradeSuccessModalControls.openModal();
        } else if (failedToUpgradeLocationIds.length === selectedLocationIds.length) {
          showFailedAlert();
        } else {
          onUpgradeSuccess({
            successfulLocationIds,
          });
          showFailedAlert(failedToUpgradeLocationIds);
        }
      },
      onError: () => {
        showFailedAlert();
      },
    });

  const isMultiLocation = modifiedSelfUpgradeEligibilityData.length > 1;

  const renderActionPrefix = useMemo(() => {
    if (isMultiLocation && selectedLocationIds.length > 0) {
      return (
        <Text weight='bold' size='medium'>
          ${FORMS_PRICE * selectedLocationIds.length}/{t('mo')}
        </Text>
      );
    } else if (selectedLocationIds.length === 0) {
      return (
        <Text color='light' size='medium'>
          {t('You must select atleast one location to continue')}
        </Text>
      );
    }
    return null;
  }, [selectedLocationIds, isMultiLocation, t]);

  const handleUpgrade = useCallback(() => {
    const user = getUser();
    const requestData: FreeTrialSubscriptionTypes.MultiSubscribeRequest = {
      coreFeatureEnum: FEATURE_ENUM,
      userDetails: {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.username ?? '',
        phone: user?.mobileNumber ?? '',
      },
      locations: selectedLocationIds.map((locationId) => ({
        slug: accessibleLocationData[locationId]?.slug || '',
        additionalLines: [],
      })),
    };
    subscribe(requestData);
  }, [selectedLocationIds, accessibleLocationData, subscribe]);

  useEffect(() => {
    if (modalProps.show && modifiedSelfUpgradeEligibilityData.length === 1) {
      if (modifiedSelfUpgradeEligibilityData[0].hasOverdueBalance) {
        overdueBalanceModalControls.openModal();
        modalProps.onClose();
      } else {
        setSelectedLocationIds(
          modifiedSelfUpgradeEligibilityData.filter(checkCanSelfSubscribe).map(({ locationId }) => locationId)
        );
      }
    }
  }, [modifiedSelfUpgradeEligibilityData, modalProps.show]);

  return (
    <>
      {isMultiLocation ? (
        <SelfUpgradeBaseModal
          featureEnum={FEATURE_ENUM}
          maxWidth={800}
          onTermsClick={subscribeTermsModalControls.openModal}
          primaryActionLabel={t('Upgrade')}
          onPrimaryAction={handleUpgrade}
          primaryActionTrackingId={getFeatureWiseTrackingId(FEATURE_ENUM, SelfUpgradeModalTrackingIds.upgradeAction)}
          isPrimaryActionDisabled={selectedLocationIds.length === 0}
          actionPrefixText={renderActionPrefix}
          isLoading={isSubscriptionInProgress || isMultiInvoiceBalanceDataLoading}
          {...modalProps}
        >
          <Trans t={t}>
            <Text size='large'>
              You are adding <strong>Digital Forms</strong> to these locations. All users on your account will have
              access to this feature.
            </Text>
          </Trans>
          {modalProps.show && !isMultiInvoiceBalanceDataLoading && (
            <SelfUpgradeLocationSelectionTable
              initialSelectedData={modifiedSelfUpgradeEligibilityData}
              data={modifiedSelfUpgradeEligibilityData}
              onChangeSelection={setSelectedLocationIds}
              price={FORMS_PRICE}
              subscriptionTerm='monthly'
            />
          )}
        </SelfUpgradeBaseModal>
      ) : (
        <SelfUpgradeBaseModal
          featureEnum={FEATURE_ENUM}
          maxWidth={600}
          onTermsClick={subscribeTermsModalControls.openModal}
          primaryActionLabel={t('Upgrade')}
          onPrimaryAction={handleUpgrade}
          primaryActionTrackingId={getFeatureWiseTrackingId(FEATURE_ENUM, SelfUpgradeModalTrackingIds.upgradeAction)}
          isLoading={isSubscriptionInProgress || isMultiInvoiceBalanceDataLoading}
          {...modalProps}
        >
          <FeatureSubscriptionChangeCard
            color='primary'
            label={t('Upgrade')}
            title={SelfUpgradeFeatureNameMap[FEATURE_ENUM] ?? ''}
            subTitle={getBillingPriceText({ price: FORMS_PRICE, subscriptionTerm: 'monthly' })}
          />
          <Text size='large'>{t('Please read and agree to the terms and conditions below.')}</Text>
        </SelfUpgradeBaseModal>
      )}
      <SubscribeTermsModal productServiceName={t('Digital Forms')} {...subscribeTermsModalControls.modalProps} />
      <SelfUpgradeSuccessModal
        featureEnum={FEATURE_ENUM}
        featureName={t('Digital Forms')}
        onboardingCallUrl={
          isMultiLocation ? FORMS_MULTI_ONBOARDER_SCHEDULING_URL : FORMS_SINGLE_ONBOARDER_SCHEDULING_URL
        }
        {...selfUpgradeSuccessModalControls.modalProps}
        onClose={selfUpgradeSuccessModalControls.closeModal}
      />
      <SelfUpgradeFailModal
        featureEnum={FEATURE_ENUM}
        partialFailedLocationIds={partialFailedLocationIds}
        {...selfUpgradeFailModalControls.modalProps}
        onClose={selfUpgradeFailModalControls.closeModal}
      />
      <SelfUpgradeOverdueBalance
        featureName={t('Digital Forms')}
        featureEnum={FEATURE_ENUM}
        {...overdueBalanceModalControls.modalProps}
      />
    </>
  );
};
